import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Typography,
  Divider,
  Row,
  Col,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AimOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Enums matching backend
const MissionType = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  EVENT: 'event',
  PERMANENT: 'permanent',
  TUTORIAL: 'tutorial',
} as const;

const MissionCategory = {
  BATTING: 'batting',
  BOWLING: 'bowling',
  FIELDING: 'fielding',
  MATCH: 'match',
  COLLECTION: 'collection',
  SOCIAL: 'social',
  PROGRESSION: 'progression',
  ECONOMY: 'economy',
} as const;

const MissionStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ARCHIVED: 'archived',
} as const;

const RewardType = {
  COINS: 'coins',
  EVO_TOKENS: 'evo_tokens',
  PREMIUM_CURRENCY: 'premium_currency',
  XP: 'xp',
  PLAYER_CARD: 'player_card',
  PACK: 'pack',
  ITEM: 'item',
} as const;

interface MissionReward {
  type: string;
  amount?: number;
  itemId?: string;
}

interface Mission {
  id: string;
  name: string;
  description: string;
  type: (typeof MissionType)[keyof typeof MissionType];
  category: (typeof MissionCategory)[keyof typeof MissionCategory];
  status: (typeof MissionStatus)[keyof typeof MissionStatus];
  requirementType: string;
  targetValue: number;
  requirementConfig?: Record<string, unknown>;
  rewards: MissionReward[];
  startDate?: string;
  endDate?: string;
  sortOrder: number;
  minLevel?: number;
  maxLevel?: number;
  targetPlatforms?: string[];
  maxCompletions?: number;
  dailyResetHour?: number;
  iconUrl?: string;
  badgeColor?: string;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  archived: 'gray',
};

const typeLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  event: 'Event',
  permanent: 'Permanent',
  tutorial: 'Tutorial',
};

const categoryLabels: Record<string, string> = {
  batting: 'Batting',
  bowling: 'Bowling',
  fielding: 'Fielding',
  match: 'Match',
  collection: 'Collection',
  social: 'Social',
  progression: 'Progression',
  economy: 'Economy',
};

const requirementTypes = [
  { value: 'score_runs', label: 'Score Runs' },
  { value: 'take_wickets', label: 'Take Wickets' },
  { value: 'win_matches', label: 'Win Matches' },
  { value: 'play_matches', label: 'Play Matches' },
  { value: 'hit_sixes', label: 'Hit Sixes' },
  { value: 'hit_fours', label: 'Hit Fours' },
  { value: 'take_catches', label: 'Take Catches' },
  { value: 'collect_cards', label: 'Collect Cards' },
  { value: 'upgrade_players', label: 'Upgrade Players' },
  { value: 'spend_coins', label: 'Spend Coins' },
  { value: 'earn_coins', label: 'Earn Coins' },
  { value: 'complete_missions', label: 'Complete Missions' },
];

const MissionsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getMissions();
      // Backend returns { data: [...], meta: {...} }
      const data = res.data?.data || res.data?.items || res.data;
      setMissions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch missions', error);
      message.error('Failed to load missions');
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const formValues = values as {
        startDate?: dayjs.Dayjs;
        endDate?: dayjs.Dayjs;
        rewardType?: string;
        rewardAmount?: number;
      } & Record<string, unknown>;

      const { startDate, endDate, rewardType, rewardAmount, ...otherValues } = formValues;

      // Build rewards array from form values
      const rewards: MissionReward[] = rewardType
        ? [{ type: rewardType, amount: rewardAmount }]
        : [];

      const payload = {
        ...otherValues,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        rewards,
      };

      if (editingId) {
        await adminApi.updateMission(editingId, payload);
        message.success('Mission updated');
      } else {
        await adminApi.createMission(payload);
        message.success('Mission created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save mission', error);
      message.error('Failed to save mission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: Mission) => {
    setEditingId(record.id);
    const primaryReward = record.rewards?.[0];
    form.setFieldsValue({
      ...record,
      startDate: record.startDate ? dayjs(record.startDate) : undefined,
      endDate: record.endDate ? dayjs(record.endDate) : undefined,
      rewardType: primaryReward?.type,
      rewardAmount: primaryReward?.amount,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteMission(id);
      message.success('Mission deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete mission', error);
      message.error('Failed to delete mission');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await adminApi.updateMission(id, { status: MissionStatus.ACTIVE });
      message.success('Mission activated');
      fetchData();
    } catch (error) {
      console.error('Failed to activate', error);
      message.error('Failed to activate mission');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await adminApi.updateMission(id, { status: MissionStatus.INACTIVE });
      message.success('Mission deactivated');
      fetchData();
    } catch (error) {
      console.error('Failed to deactivate', error);
      message.error('Failed to deactivate mission');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Mission) => (
        <div>
          <Text strong>{name}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description?.substring(0, 50)}...
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="purple">{typeLabels[type] || type}</Tag>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag>{categoryLabels[category] || category}</Tag>,
    },
    {
      title: 'Target',
      key: 'target',
      render: (_: unknown, record: Mission) => (
        <span>
          {record.targetValue} ({record.requirementType})
        </span>
      ),
    },
    {
      title: 'Rewards',
      key: 'rewards',
      render: (_: unknown, record: Mission) => {
        const reward = record.rewards?.[0];
        return reward ? (
          <Tag color="gold">
            {reward.amount} {reward.type}
          </Tag>
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Mission) => (
        <Space>
          {record.status === 'active' ? (
            <Button
              icon={<PauseCircleOutlined />}
              size="small"
              onClick={() => handleDeactivate(record.id)}
            >
              Deactivate
            </Button>
          ) : (
            <Button
              icon={<PlayCircleOutlined />}
              size="small"
              type="primary"
              onClick={() => handleActivate(record.id)}
            >
              Activate
            </Button>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this mission?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <AimOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Missions
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Create Mission
          </Button>
        }
      >
        <Table
          dataSource={missions}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Mission' : 'Create Mission'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={800}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="configVersionId"
            label="Config Version"
            extra="Leave empty to show this to every build. Pick a config version to limit it to the app versions mapped to that version."
          >
            <ConfigVersionSelect style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Mission name" />
          </Form.Item>

          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={2} placeholder="Mission description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="type" label="Mission Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {Object.entries(MissionType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {typeLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select placeholder="Select category">
                  {Object.entries(MissionCategory).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {categoryLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue={MissionStatus.INACTIVE}>
                <Select>
                  {Object.entries(MissionStatus).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Requirements</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="requirementType"
                label="Requirement Type"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select requirement">
                  {requirementTypes.map((req) => (
                    <Select.Option key={req.value} value={req.value}>
                      {req.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="targetValue" label="Target Value" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g., 100" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Rewards</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rewardType" label="Reward Type" rules={[{ required: true }]}>
                <Select placeholder="Select reward type">
                  {Object.entries(RewardType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase().replace('_', ' ')}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rewardAmount" label="Reward Amount" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} placeholder="e.g., 1000" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Scheduling</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="startDate" label="Start Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="endDate" label="End Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sortOrder" label="Sort Order" initialValue={0}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Targeting</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="minLevel" label="Min Level">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxLevel" label="Max Level">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxCompletions" label="Max Completions">
                <InputNumber style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="iconUrl" label="Icon URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="badgeColor" label="Badge Color">
                <Input placeholder="#FF5733" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default MissionsPage;
